import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Take full control of scroll position across route changes.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Remembered scroll offsets, keyed by history entry key.
const positions = new Map();

function setScroll(y) {
  const root = document.documentElement;
  const prev = root.style.scrollBehavior;
  root.style.scrollBehavior = "auto"; // defeat the global `scroll-behavior: smooth`
  window.scrollTo(0, y);
  root.scrollTop = y;
  if (document.body) document.body.scrollTop = y;
  root.style.scrollBehavior = prev;
}

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType();
  const keyRef = useRef(key || "default");
  const restoringRef = useRef(false);

  // One persistent listener that records the live scroll for the CURRENT entry
  // (via a ref, so it always writes to the right key even during transitions).
  useEffect(() => {
    const onScroll = () => { if (!restoringRef.current) positions.set(keyRef.current, window.scrollY); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    keyRef.current = key || "default";

    if (hash) {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) { el.scrollIntoView(); return; }
    }

    // Back / Forward (POP): return to exactly where the user was. New pages and
    // "next chapter" navigations (PUSH/REPLACE) always open at the top as before.
    if (navType === "POP" && positions.has(keyRef.current)) {
      const y = positions.get(keyRef.current);
      restoringRef.current = true;
      setScroll(y);
      // The page often remounts short and grows as async data streams in, which
      // clamps an early restore — keep re-applying (up to ~3s) until it sticks.
      let tries = 0;
      const iv = setInterval(() => {
        setScroll(y);
        if (Math.abs(window.scrollY - y) < 2 || ++tries > 30) { clearInterval(iv); restoringRef.current = false; }
      }, 100);
      return () => { clearInterval(iv); restoringRef.current = false; };
    }

    setScroll(0);
    const id = requestAnimationFrame(() => setScroll(0));
    return () => cancelAnimationFrame(id);
  }, [pathname, hash, key, navType]);

  return null;
}
